import { useState, useEffect } from 'react';
import { Layout } from '../components/layout/Layout';
import { timeclockAPI, usersAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '../components/ui/table';
import {
    Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle
} from '../components/ui/dialog';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { Clock, Users, KeyRound, PenLine, Loader2, Plus, Trash2 } from 'lucide-react';

function formatDuration(hours) {
    if (!hours) return '—';
    const h = Math.floor(hours);
    const m = Math.round((hours - h) * 60);
    return `${h}h ${m}m`;
}

function formatTime(isoString) {
    if (!isoString) return '—';
    return new Date(isoString).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

function formatDate(isoString) {
    if (!isoString) return '—';
    return new Date(isoString).toLocaleDateString([], { month: 'short', day: 'numeric' });
}

export default function TimeClock() {
    const [entries, setEntries] = useState([]);
    const [pins, setPins] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('today');

    // PIN dialog
    const [pinDialogOpen, setPinDialogOpen] = useState(false);
    const [selectedUserId, setSelectedUserId] = useState('');
    const [pinValue, setPinValue] = useState('');
    const [savingPin, setSavingPin] = useState(false);

    // Edit dialog
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [editEntry, setEditEntry] = useState(null);
    const [editClockIn, setEditClockIn] = useState('');
    const [editClockOut, setEditClockOut] = useState('');
    const [editReason, setEditReason] = useState('');
    const [savingEdit, setSavingEdit] = useState(false);

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            const [entriesRes, pinsRes] = await Promise.all([
                timeclockAPI.getEntries(),
                timeclockAPI.listPins()
            ]);
            setEntries(entriesRes.data);
            setPins(pinsRes.data);
        } catch (error) {
            toast.error('Failed to load timeclock data');
        } finally {
            setLoading(false);
        }
    };

    const todayStr = new Date().toISOString().split('T')[0];
    const todayEntries = entries.filter(e => e.clock_in?.startsWith(todayStr));
    const activeEntries = entries.filter(e => e.status === 'active');
    const totalHoursToday = todayEntries.reduce((sum, e) => sum + (e.total_hours || 0), 0);

    // Calculate active durations
    const now = new Date();
    const activeDurations = activeEntries.reduce((sum, e) => {
        const clockIn = new Date(e.clock_in);
        return sum + (now - clockIn) / 3600000;
    }, 0);

    const handleSetPin = async () => {
        if (!selectedUserId || pinValue.length !== 4 || !pinValue.match(/^\d{4}$/)) {
            toast.error('Please select an employee and enter a 4-digit PIN');
            return;
        }
        setSavingPin(true);
        try {
            await timeclockAPI.createPin({ user_id: selectedUserId, pin: pinValue });
            toast.success('PIN set successfully');
            setPinDialogOpen(false);
            setPinValue('');
            setSelectedUserId('');
            const pinsRes = await timeclockAPI.listPins();
            setPins(pinsRes.data);
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to set PIN');
        } finally {
            setSavingPin(false);
        }
    };

    const handleDeletePin = async (userId) => {
        try {
            await timeclockAPI.deletePin(userId);
            toast.success('PIN deactivated');
            const pinsRes = await timeclockAPI.listPins();
            setPins(pinsRes.data);
        } catch (err) {
            toast.error('Failed to deactivate PIN');
        }
    };

    const openEditDialog = (entry) => {
        setEditEntry(entry);
        setEditClockIn(entry.clock_in ? new Date(entry.clock_in).toISOString().slice(0, 16) : '');
        setEditClockOut(entry.clock_out ? new Date(entry.clock_out).toISOString().slice(0, 16) : '');
        setEditReason('');
        setEditDialogOpen(true);
    };

    const handleSaveEdit = async () => {
        if (editReason.length < 5) {
            toast.error('Reason must be at least 5 characters');
            return;
        }
        setSavingEdit(true);
        try {
            const payload = { reason: editReason };
            if (editClockIn) payload.clock_in = new Date(editClockIn).toISOString();
            if (editClockOut) payload.clock_out = new Date(editClockOut).toISOString();
            await timeclockAPI.editEntry(editEntry.id, payload);
            toast.success('Entry updated');
            setEditDialogOpen(false);
            fetchData();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to update entry');
        } finally {
            setSavingEdit(false);
        }
    };

    const displayEntries = activeTab === 'today' ? todayEntries : entries;

    if (loading) {
        return (
            <Layout title="Time Clock">
                <div className="flex items-center justify-center h-64">
                    <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                </div>
            </Layout>
        );
    }

    return (
        <Layout title="Time Clock">
            <div className="space-y-6" data-testid="timeclock-page">
                {/* Summary Cards */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <Card>
                        <CardContent className="p-4 flex items-center justify-between">
                            <div>
                                <p className="text-sm text-muted-foreground">Currently Clocked In</p>
                                <p className="text-3xl font-bold font-mono" data-testid="active-count">{activeEntries.length}</p>
                            </div>
                            <div className="w-12 h-12 rounded-xl bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
                                <Users className="h-6 w-6 text-emerald-600" />
                            </div>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardContent className="p-4 flex items-center justify-between">
                            <div>
                                <p className="text-sm text-muted-foreground">Today's Total Hours</p>
                                <p className="text-3xl font-bold font-mono" data-testid="today-hours">{formatDuration(totalHoursToday + activeDurations)}</p>
                            </div>
                            <div className="w-12 h-12 rounded-xl bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                                <Clock className="h-6 w-6 text-blue-600" />
                            </div>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardContent className="p-4 flex items-center justify-between">
                            <div>
                                <p className="text-sm text-muted-foreground">PINs Assigned</p>
                                <p className="text-3xl font-bold font-mono" data-testid="pins-count">{pins.filter(p => p.has_pin).length} / {pins.length}</p>
                            </div>
                            <div className="w-12 h-12 rounded-xl bg-violet-100 dark:bg-violet-900/30 flex items-center justify-center">
                                <KeyRound className="h-6 w-6 text-violet-600" />
                            </div>
                        </CardContent>
                    </Card>
                </div>

                <Tabs value={activeTab} onValueChange={setActiveTab}>
                    <div className="flex items-center justify-between">
                        <TabsList>
                            <TabsTrigger value="today" data-testid="tab-today">Today</TabsTrigger>
                            <TabsTrigger value="all" data-testid="tab-all">All Entries</TabsTrigger>
                            <TabsTrigger value="pins" data-testid="tab-pins">PIN Management</TabsTrigger>
                        </TabsList>
                    </div>

                    {/* Today / All Entries Tab */}
                    <TabsContent value={activeTab === 'pins' ? 'pins' : activeTab}>
                        {activeTab !== 'pins' && (
                            <Card>
                                <CardContent className="p-0">
                                    <Table>
                                        <TableHeader>
                                            <TableRow>
                                                <TableHead>Status</TableHead>
                                                <TableHead>Employee</TableHead>
                                                <TableHead>Date</TableHead>
                                                <TableHead>Clock In</TableHead>
                                                <TableHead>Clock Out</TableHead>
                                                <TableHead>Hours</TableHead>
                                                <TableHead>Flag</TableHead>
                                                <TableHead className="w-[60px]"></TableHead>
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {displayEntries.map((entry) => {
                                                let duration = entry.total_hours;
                                                if (entry.status === 'active') {
                                                    duration = (new Date() - new Date(entry.clock_in)) / 3600000;
                                                }
                                                return (
                                                    <TableRow key={entry.id} data-testid={`entry-row-${entry.id}`}>
                                                        <TableCell>
                                                            {entry.status === 'active' ? (
                                                                <Badge className="bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400">Active</Badge>
                                                            ) : entry.status === 'edited' ? (
                                                                <Badge variant="outline" className="border-amber-500 text-amber-600">Edited</Badge>
                                                            ) : (
                                                                <Badge variant="secondary">Done</Badge>
                                                            )}
                                                        </TableCell>
                                                        <TableCell className="font-medium">{entry.user_name}</TableCell>
                                                        <TableCell className="text-muted-foreground">{formatDate(entry.clock_in)}</TableCell>
                                                        <TableCell className="font-mono">{formatTime(entry.clock_in)}</TableCell>
                                                        <TableCell className="font-mono">{formatTime(entry.clock_out)}</TableCell>
                                                        <TableCell className="font-mono font-semibold">{formatDuration(duration)}</TableCell>
                                                        <TableCell>
                                                            {entry.flag_reason === 'overtime' && <Badge variant="destructive">OT</Badge>}
                                                            {entry.flag_reason === 'manual_edit' && <Badge variant="outline">Edited</Badge>}
                                                        </TableCell>
                                                        <TableCell>
                                                            <Button variant="ghost" size="icon" onClick={() => openEditDialog(entry)} data-testid={`edit-entry-${entry.id}`}>
                                                                <PenLine className="h-4 w-4" />
                                                            </Button>
                                                        </TableCell>
                                                    </TableRow>
                                                );
                                            })}
                                            {displayEntries.length === 0 && (
                                                <TableRow>
                                                    <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                                                        No time entries found
                                                    </TableCell>
                                                </TableRow>
                                            )}
                                        </TableBody>
                                    </Table>
                                </CardContent>
                            </Card>
                        )}
                    </TabsContent>

                    {/* PIN Management Tab */}
                    <TabsContent value="pins">
                        <Card>
                            <CardHeader className="flex flex-row items-center justify-between pb-4">
                                <CardTitle className="text-base">Employee PINs</CardTitle>
                                <Button onClick={() => setPinDialogOpen(true)} size="sm" data-testid="assign-pin-btn">
                                    <Plus className="h-4 w-4 mr-1" /> Assign PIN
                                </Button>
                            </CardHeader>
                            <CardContent className="p-0">
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead>Employee</TableHead>
                                            <TableHead>Role</TableHead>
                                            <TableHead>PIN Status</TableHead>
                                            <TableHead className="w-[60px]"></TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {pins.map((p) => (
                                            <TableRow key={p.user_id} data-testid={`pin-row-${p.user_id}`}>
                                                <TableCell className="font-medium">{p.user_name}</TableCell>
                                                <TableCell><Badge variant="outline" className="capitalize">{p.role}</Badge></TableCell>
                                                <TableCell>
                                                    {p.has_pin ? (
                                                        <Badge className="bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400">Assigned</Badge>
                                                    ) : (
                                                        <Badge variant="secondary">No PIN</Badge>
                                                    )}
                                                    {p.is_locked && <Badge variant="destructive" className="ml-2">Locked</Badge>}
                                                </TableCell>
                                                <TableCell>
                                                    {p.has_pin && (
                                                        <Button variant="ghost" size="icon" onClick={() => handleDeletePin(p.user_id)} className="text-muted-foreground hover:text-red-600" data-testid={`delete-pin-${p.user_id}`}>
                                                            <Trash2 className="h-4 w-4" />
                                                        </Button>
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </CardContent>
                        </Card>
                    </TabsContent>
                </Tabs>
            </div>

            {/* Assign PIN Dialog */}
            <Dialog open={pinDialogOpen} onOpenChange={setPinDialogOpen}>
                <DialogContent className="sm:max-w-md" data-testid="assign-pin-dialog">
                    <DialogHeader>
                        <DialogTitle>Assign Employee PIN</DialogTitle>
                        <DialogDescription>Enter a 4-digit PIN (or last 4 of SSN) for the employee.</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div>
                            <label className="text-sm font-medium mb-1 block">Employee</label>
                            <Select value={selectedUserId} onValueChange={setSelectedUserId}>
                                <SelectTrigger data-testid="pin-employee-select">
                                    <SelectValue placeholder="Select employee" />
                                </SelectTrigger>
                                <SelectContent>
                                    {pins.map(p => (
                                        <SelectItem key={p.user_id} value={p.user_id}>{p.user_name} ({p.role})</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <label className="text-sm font-medium mb-1 block">4-Digit PIN</label>
                            <Input
                                type="text"
                                inputMode="numeric"
                                maxLength={4}
                                placeholder="e.g. 1234"
                                value={pinValue}
                                onChange={e => setPinValue(e.target.value.replace(/\D/g, '').slice(0, 4))}
                                className="text-center text-2xl tracking-[0.5em] font-mono"
                                data-testid="pin-input"
                            />
                            <p className="text-xs text-muted-foreground mt-1">Tip: You can use the last 4 digits of the employee's SSN</p>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setPinDialogOpen(false)}>Cancel</Button>
                        <Button onClick={handleSetPin} disabled={savingPin} data-testid="save-pin-btn">
                            {savingPin && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                            Set PIN
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Edit Entry Dialog */}
            <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
                <DialogContent className="sm:max-w-md" data-testid="edit-entry-dialog">
                    <DialogHeader>
                        <DialogTitle>Edit Time Entry</DialogTitle>
                        <DialogDescription>
                            {editEntry?.user_name} — {formatDate(editEntry?.clock_in)}
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div>
                            <label className="text-sm font-medium mb-1 block">Clock In</label>
                            <Input type="datetime-local" value={editClockIn} onChange={e => setEditClockIn(e.target.value)} data-testid="edit-clock-in" />
                        </div>
                        <div>
                            <label className="text-sm font-medium mb-1 block">Clock Out</label>
                            <Input type="datetime-local" value={editClockOut} onChange={e => setEditClockOut(e.target.value)} data-testid="edit-clock-out" />
                        </div>
                        <div>
                            <label className="text-sm font-medium mb-1 block">Reason for Edit *</label>
                            <Input placeholder="e.g. Employee forgot to clock out" value={editReason} onChange={e => setEditReason(e.target.value)} data-testid="edit-reason" />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setEditDialogOpen(false)}>Cancel</Button>
                        <Button onClick={handleSaveEdit} disabled={savingEdit} data-testid="save-edit-btn">
                            {savingEdit && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                            Save Changes
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </Layout>
    );
}
